<template>
    <section class="object-gallery-panel">
        <button
            type="button"
            class="object-gallery-panel__header"
            :aria-expanded="String(!collapsed)"
            @click="toggle"
        >
            <span>
                <i class="fas fa-images mr-2 text-info"></i>
                Галерея
            </span>
            <i :class="collapsed ? 'fas fa-chevron-down' : 'fas fa-chevron-up'"></i>
        </button>

        <b-collapse :visible="!collapsed">
            <div v-if="initialized" class="object-gallery-panel__body">
                <div class="object-gallery-toolbar">
                    <b-form-input
                        v-model="filter"
                        type="search"
                        size="sm"
                        class="object-gallery-search"
                        placeholder="Поиск по смене или дате"
                        autocomplete="off"
                        debounce="1000"
                    ></b-form-input>
                    <b-button-group size="sm" aria-label="Статус отчётов галереи">
                        <b-button
                            v-for="option in reportStatusOptions"
                            :key="option.value"
                            :variant="reportStatus === option.value ? option.variant : 'outline-secondary'"
                            @click="reportStatus = option.value"
                        >
                            {{ option.text }}
                        </b-button>
                    </b-button-group>
                </div>

                <GalleryWorkFilter v-model="workFilters" :process-id="processId" :report-status="reportStatus" :active="!collapsed" />

                <div v-if="initialLoading" class="text-center text-muted py-4">
                    <b-spinner small class="mr-1"></b-spinner>
                    Загрузка галереи
                </div>

                <b-alert v-else-if="galleryError && !shifts.length" show variant="danger">
                    <div class="d-flex flex-wrap align-items-center justify-content-between">
                        <span>{{ galleryError }}</span>
                        <b-button size="sm" variant="outline-danger" class="ml-2" @click="retryInitialLoad">
                            Повторить
                        </b-button>
                    </div>
                </b-alert>

                <div v-else-if="!shifts.length" class="object-gallery-empty">
                    <i class="fas fa-images"></i>
                    <span>{{ emptyText }}</span>
                </div>

                <div v-else-if="!collapsed" class="object-gallery-scroll">
                    <ShiftImages
                        v-for="(shift, index) in shifts"
                        :key="getShiftKey(shift, index)"
                        :data="shift"
                        :all-shifts="shifts"
                        :shift-index="index"
                        :shifts-completed="shiftsCompleted"
                        :load-more-shifts="loadMoreShifts"
                        class="my-1"
                    />

                    <b-alert v-if="galleryError" show variant="danger" class="mt-2">
                        <div class="d-flex flex-wrap align-items-center justify-content-between">
                            <span>{{ galleryError }}</span>
                            <b-button
                                size="sm"
                                variant="outline-danger"
                                class="ml-2"
                                :disabled="loadingMore"
                                @click="retryLoadMore"
                            >
                                <b-spinner v-if="loadingMore" small class="mr-1"></b-spinner>
                                Повторить
                            </b-button>
                        </div>
                    </b-alert>

                    <infinite-loading
                        v-else-if="!shiftsCompleted && !loadingMore"
                        :identifier="infiniteId"
                        @infinite="infiniteHandler"
                    ></infinite-loading>
                    <div v-else-if="loadingMore" class="text-center text-muted py-2">
                        <b-spinner small class="mr-1"></b-spinner>
                        Загрузка следующих смен
                    </div>
                </div>
            </div>
        </b-collapse>
    </section>
</template>

<script>
import axios from 'axios';
import {createGalleryImageLoader} from '@/views/Gallery/galleryImageLoader';
import GalleryWorkFilter from '@/views/Gallery/GalleryWorkFilter.vue';
import InfiniteLoading from 'vue-infinite-loading';
import ShiftImages from '@/views/Gallery/ObjectImages/ShiftImages/ShiftImages.vue';

export default {
    name: `ObjectGalleryBlock`,
    provide() {
        this._imageLoader = createGalleryImageLoader();
        return {galleryImageLoader: this._imageLoader};
    },
    components: {
        GalleryWorkFilter,
        InfiniteLoading,
        ShiftImages
    },
    props: {
        processId: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            collapsed: true,
            initialized: false,
            filter: ``,
            workFilters: {workIds: [], groupIds: []},
            reportStatus: `all`,
            reportStatusOptions: [
                {value: `all`, text: `Все`, variant: `primary`},
                {value: `active`, text: `Действующие`, variant: `success`},
                {value: `annulled`, text: `Аннулированные`, variant: `danger`}
            ],
            shifts: [],
            skip: 0,
            shiftsCompleted: false,
            initialLoading: false,
            loadingMore: false,
            galleryError: null,
            loadingPromise: null,
            requestGeneration: 0,
            infiniteId: 0
        };
    },
    computed: {
        galleryQuery() {
            const query = String(this.filter || ``).trim();
            return query || null;
        },
        shiftLimit() {
            return this.isMobileViewport() ? 3 : 5;
        },
        mediaLimit() {
            return this.isMobileViewport() ? 60 : 100;
        },
        emptyText() {
            return this.galleryQuery || this.workFilters.workIds.length || this.workFilters.groupIds.length
                ? `По заданным условиям фотографии не найдены`
                : `По объекту пока нет фотографий`;
        }
    },
    watch: {
        workFilters: {deep: true, handler() { this.onFiltersChanged(); }},
        galleryQuery() {
            this.onFiltersChanged();
        },
        reportStatus() {
            this.onFiltersChanged();
        },
        processId() {
            this.resetForAnotherProcess();
        }
    },
    beforeDestroy() {
        this.requestGeneration++;
        if (this._galleryRequest) this._galleryRequest.cancel();
        this._imageLoader.destroy();
    },
    methods: {
        isMobileViewport() {
            return Boolean(this.$store.state.isMobile) ||
                (typeof window !== `undefined` && window.innerWidth < 768);
        },
        getShiftKey(shift, index) {
            return shift && (shift.shiftId || shift.shift_id)
                ? (shift.shiftId || shift.shift_id)
                : index;
        },
        toggle() {
            this.collapsed = !this.collapsed;
            if (this.collapsed) {
                this.requestGeneration++;
                if (this._galleryRequest) this._galleryRequest.cancel();
                this.loadingPromise = null;
                this.initialLoading = false;
                this.loadingMore = false;
                this.infiniteId++;
            } else if (!this.initialized || (!this.shifts.length && !this.shiftsCompleted)) {
                this.initialized = true;
                this.resetAndLoad();
            }
        },
        resetForAnotherProcess() {
            this.requestGeneration += 1;
            if (this._galleryRequest) this._galleryRequest.cancel();
            this.collapsed = true;
            this.initialized = false;
            this.filter = ``;
            this.workFilters = {workIds: [], groupIds: []};
            this.reportStatus = `all`;
            this.resetState();
        },
        resetState() {
            this.shifts = [];
            this.skip = 0;
            this.shiftsCompleted = false;
            this.initialLoading = false;
            this.loadingMore = false;
            this.galleryError = null;
            this.loadingPromise = null;
            this.infiniteId += 1;
        },
        onFiltersChanged() {
            if (this.initialized) {
                this.resetAndLoad();
            }
        },
        async resetAndLoad() {
            this.requestGeneration += 1;
            if (this._galleryRequest) this._galleryRequest.cancel();
            const generation = this.requestGeneration;
            this.resetState();
            if (this.collapsed) return;
            this.initialLoading = true;
            try {
                await this.loadMoreShifts(generation, true);
            } catch (err) {
                // loadMoreShifts exposes the error inside the expanded block.
            } finally {
                if (generation === this.requestGeneration) {
                    this.initialLoading = false;
                }
            }
        },
        galleryErrorText(value) {
            const responseData = value && value.response && value.response.data;
            if (responseData) {
                return responseData.msg || responseData.message || responseData;
            }
            if (value && value.message) {
                return value.message;
            }
            return `Не удалось загрузить галерею`;
        },
        async requestShifts() {
            const request = axios.CancelToken.source();
            this._galleryRequest = request;
            const data = await this.$store.dispatch(`getImagesForBuilding`, {
                cancelToken: request.token,
                id: this.processId,
                limit: this.shiftLimit,
                skip: this.skip,
                query_building: this.galleryQuery,
                search_scope: `shift`,
                media_limit: this.mediaLimit,
                report_status: this.reportStatus,
                work_ids: this.workFilters.workIds.join(',') || undefined,
                work_group_ids: this.workFilters.groupIds.join(',') || undefined
            });
            if (axios.isCancel(data)) throw data;
            if (!Array.isArray(data)) {
                throw new Error(this.galleryErrorText(data));
            }
            return data;
        },
        async loadMoreShifts(requestGeneration = this.requestGeneration, initial = false) {
            if (this.collapsed || this.shiftsCompleted || !this.processId || requestGeneration !== this.requestGeneration) {
                return [];
            }
            if (this.loadingPromise) {
                return this.loadingPromise;
            }

            if (!initial) {
                this.loadingMore = true;
            }
            this.galleryError = null;

            const promise = this.requestShifts();
            this.loadingPromise = promise;
            try {
                const items = await promise;
                if (requestGeneration !== this.requestGeneration) {
                    return [];
                }
                if (items.length) {
                    items.forEach(item => this.shifts.push(item));
                    this.skip += items.length;
                } else {
                    this.shiftsCompleted = true;
                }
                return items;
            } catch (err) {
                if (requestGeneration === this.requestGeneration) {
                    this.galleryError = this.galleryErrorText(err);
                }
                throw err;
            } finally {
                if (this.loadingPromise === promise) {
                    this.loadingPromise = null;
                }
                if (requestGeneration === this.requestGeneration && !initial) {
                    this.loadingMore = false;
                }
            }
        },
        async infiniteHandler($state) {
            const generation = this.requestGeneration;
            try {
                const items = await this.loadMoreShifts();
                if (generation !== this.requestGeneration) return;
                if (items.length) {
                    $state.loaded();
                } else {
                    $state.complete();
                }
            } catch (err) {
                if (generation === this.requestGeneration) $state.complete();
            }
        },
        retryInitialLoad() {
            this.resetAndLoad();
        },
        async retryLoadMore() {
            this.galleryError = null;
            try {
                await this.loadMoreShifts();
                this.infiniteId += 1;
            } catch (err) {
                // loadMoreShifts already exposes the error inside the block.
            }
        }
    }
};
</script>

<style scoped>
.object-gallery-panel {
    background: #fff;
    border: 1px solid #e5e9ef;
    border-radius: 10px;
    margin-bottom: 14px;
}

.object-gallery-panel__header {
    align-items: center;
    background: transparent;
    border: 0;
    color: #212529;
    display: flex;
    font-size: 1rem;
    font-weight: 600;
    justify-content: space-between;
    padding: 12px 14px;
    text-align: left;
    width: 100%;
}

.object-gallery-panel__header:hover,
.object-gallery-panel__header:focus {
    background: #f8fafc;
    outline: none;
}

.object-gallery-panel__body {
    border-top: 1px solid #edf0f4;
    padding: 12px 14px 14px;
}

.object-gallery-toolbar {
    align-items: center;
    display: flex;
    gap: 8px;
    margin-bottom: 10px;
}

.object-gallery-search {
    min-width: 220px;
}

.object-gallery-scroll {
    max-height: 70vh;
    overflow-y: auto;
    padding: 0 4px;
    -webkit-overflow-scrolling: touch;
}

.object-gallery-empty {
    align-items: center;
    color: #6c757d;
    display: flex;
    flex-direction: column;
    gap: 8px;
    justify-content: center;
    padding: 28px 12px;
    text-align: center;
}

.object-gallery-empty i {
    font-size: 1.5rem;
}

@media (max-width: 767.98px) {
    .object-gallery-panel {
        border-radius: 8px;
    }

    .object-gallery-panel__body {
        padding-left: 6px;
        padding-right: 6px;
    }

    .object-gallery-toolbar {
        align-items: stretch;
        flex-direction: column;
        padding: 0 6px;
    }

    .object-gallery-search {
        min-width: 0;
        width: 100%;
    }

    .object-gallery-toolbar .btn-group {
        display: flex;
        width: 100%;
    }

    .object-gallery-toolbar .btn-group .btn {
        flex: 1 1 0;
        padding-left: 4px;
        padding-right: 4px;
    }

    .object-gallery-scroll {
        max-height: none;
        overflow: visible;
        padding: 0;
    }
}
</style>
